import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getVisibilityFactors, createVisibilityFactor, updateVisibilityFactor, deleteVisibilityFactor } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const GetVisibilityFactors = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [visibilityFactors, setVisibilityFactors] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchVisibilityFactors = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getVisibilityFactors();
            if (response && response.success) {
                setVisibilityFactors(response.data);
            } else {
                setError("Failed to load visibility factors.");
            }
        } catch (err) {
            setError("Failed to fetch visibility factors. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVisibilityFactors();
    }, [fetchVisibilityFactors]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                title: item.title,
                description: item.description,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: item.isActive !== undefined ? item.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                title: '',
                description: '',
                displayOrder: '1',
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({
            title: '',
            description: '',
            displayOrder: '1',
            isActive: true
        });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        if (name === 'displayOrder') {
            if (value !== "" && (!/^\d+$/.test(value) || Number(value) > 999)) {
                return;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!formData.title.trim()) {
            errors.title = "Title is required";
        }
        if (!formData.description.trim() || formData.description === '<p><br></p>') {
            errors.description = "Description is required";
        }
        if (formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined && (Number(formData.displayOrder) > 999 || Number(formData.displayOrder) < 1)) {
            errors.displayOrder = "Display order must be between 1 and 999.";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setSubmitting(true);
        setError(null);
        setValidationErrors({});

        const payload = {
            title: formData.title,
            description: formData.description,
            isActive: formData.isActive,
            ...(formData.displayOrder !== "" && formData.displayOrder !== null && formData.displayOrder !== undefined ? { displayOrder: Number(formData.displayOrder) } : {})
        };

        try {
            if (isEdit) {
                await updateVisibilityFactor(currentItem.id, payload);
            } else {
                await createVisibilityFactor(payload);
            }

            handleCloseModal();
            fetchVisibilityFactors();
            showSnackbar(`Visibility factor ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = serverError?.message || `Failed to ${isEdit ? 'update' : 'create'} visibility factor`;
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'title' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ title: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteVisibilityFactor(selectedId);
                if (response && response.success) {
                    showSnackbar("Visibility factor deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchVisibilityFactors();
                } else {
                    setDeleteError(response?.message || "Failed to delete visibility factor");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete visibility factor";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Visibility Factors
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={20} />}
                            onClick={() => handleOpenModal()}
                            sx={{ bgcolor: '#ffae42', px: 3, color: '#fff', borderRadius: '8px' }}
                        >
                            Add Visibility Factor
                        </Button>
                    </PermissionWrapper>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1100 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '160px' }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5 }}>Description</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : visibilityFactors.length > 0 ? (
                                    visibilityFactors.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ color: '#666', py: 1.25, px: 1.5, fontSize: '0.8rem', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>
                                                {item.title}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, color: '#666', maxWidth: '350px' }}>
                                                <Typography
                                                    sx={{
                                                        color: '#666',
                                                        fontSize: '0.8rem',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: item.description || "No description available."
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: item.isActive ? '#E8F5E9' : '#FEECEB',
                                                    color: item.isActive ? '#2E7D32' : '#F44336',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {item.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3, color: '#AAA', fontSize: '0.8rem' }}>
                                            No visibility factors found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222' }}>
                        {isEdit ? 'Edit Visibility Factor' : 'Add Visibility Factor'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Title *"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="e.g. Featured Listing"
                                autoFocus
                                error={!!validationErrors.title}
                                helperText={validationErrors.title}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label="Display Order"
                                name="displayOrder"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="Enter display order (1 - 999)"
                                inputProps={{ min: 1, max: 999, onWheel: (e) => e.target.blur() }}
                                error={Boolean(validationErrors.displayOrder)}
                                helperText={validationErrors.displayOrder}
                                size="small"
                                sx={{
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                        WebkitAppearance: 'none',
                                        margin: 0,
                                    },
                                    '& input[type=number]': {
                                        MozAppearance: 'textfield',
                                    }
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffae42' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ffae42' }
                                        }}
                                    />
                                }
                                label={<span style={{ fontSize: '0.85rem' }}>Active Status</span>}
                            />
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                    Description *
                                </Typography>
                                <ReactQuill
                                    theme="snow"
                                    value={formData.description}
                                    onChange={(value) => {
                                        setFormData(prev => ({ ...prev, description: value }));
                                        if (validationErrors.description) {
                                            setValidationErrors(prev => {
                                                const newErrors = { ...prev };
                                                delete newErrors.description;
                                                return newErrors;
                                            });
                                        }
                                    }}
                                    style={{ height: '200px', marginBottom: '5px' }}
                                    modules={{
                                        toolbar: [
                                            [{ 'header': [1, 2, false] }],
                                            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                            ['link', 'clean']
                                        ],
                                    }}
                                />
                                {validationErrors.description && (
                                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, mb: 2 }}>
                                        {validationErrors.description}
                                    </Typography>
                                )}
                                <Box sx={{ height: '45px' }} />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222' }}>Cancel</Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{ bgcolor: '#ffae42', color: '#fff' }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Visibility Factor"
                    description="Are you sure you want to delete this visibility factor? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetVisibilityFactors;

